import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "391",
  "slug": "what-are-landsat-satellites",
  "title": "What Are the Landsat Satellites? The Program That Has Watched Earth Since 1972",
  "excerpt": "For over half a century, the Landsat satellites have photographed the entire surface of the planet again and again. Here is what they are, what they see, and why that long record is so valuable.",
  "date": "2026-06-09",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXRlbGxpdGUlMjBpbiUyMG9yYml0fGVufDF8MHx8fDE3ODA5OTI4MzN8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "Landsat is a long-running Earth-observation program, started in 1972, that uses satellites to photograph the entire land surface of the planet on a repeating schedule. Its decades-long, freely available archive lets scientists track how forests, cities, farms, glaciers, and coastlines change over time.",
  "content": [
    {
      "type": "paragraph",
      "text": "Almost every striking satellite image you have seen of a shrinking glacier, a growing city, or a vanishing forest probably traces back, at least in part, to a single program: Landsat. Quietly orbiting since 1972, the Landsat satellites have built the longest continuous record of Earth’s surface ever assembled. Here is why that matters."
    },
    {
      "type": "heading",
      "text": "A Five-Decade Photo Diary of the Planet"
    },
    {
      "type": "paragraph",
      "text": "Landsat is a joint Earth-observation program that has kept at least one satellite circling the planet for over fifty years, with new generations launched as older ones retire. Each satellite orbits from pole to pole while Earth rotates underneath it, so over a couple of weeks it images the entire land surface, then starts again. The payoff is not any single photo but the time-lapse: the same place, captured over and over, year after year."
    },
    {
      "type": "paragraph",
      "text": "That long, consistent record is what makes Landsat special. A one-off image shows you what a place looks like. A fifty-year archive shows you how it has changed, and change is where the real science lives."
    },
    {
      "type": "heading",
      "text": "What Landsat Actually Sees"
    },
    {
      "type": "paragraph",
      "text": "Landsat satellites do not just take ordinary photographs. They carry instruments that record light in multiple bands, including wavelengths the human eye cannot see, such as near-infrared and thermal infrared. By combining these bands, scientists can pull out information invisible in a normal snapshot:"
    },
    {
      "type": "list",
      "items": [
        "Vegetation health — plants reflect strongly in near-infrared, so the data reveals where crops and forests are thriving or stressed.",
        "Water and moisture — different bands separate open water, wet soil, and dry land.",
        "Heat — thermal bands map surface temperature, from city heat islands to volcanic activity.",
        "Land cover — distinguishing forest, farmland, city, and bare ground across whole continents."
      ]
    },
    {
      "type": "heading",
      "text": "Why the Long Record Is So Powerful"
    },
    {
      "type": "paragraph",
      "text": "Because Landsat has been watching the same places for decades, it can answer questions no single image ever could. Researchers use the archive to measure deforestation in the Amazon, track the retreat of glaciers, watch cities sprawl outward, follow the shrinking of lakes and inland seas, and monitor how farmland expands or fails. The consistency matters: because the measurements are made the same way over time, the changes you see are real, not artifacts of switching instruments."
    },
    {
      "type": "heading",
      "text": "Free Data That Changed the Field"
    },
    {
      "type": "paragraph",
      "text": "One of the most important things about Landsat happened on the ground, not in orbit. In 2008, the full Landsat archive was opened to the public for free. That decision unleashed a wave of research and applications, because suddenly anyone, scientists, farmers, students, and developers, could access decades of imagery without a paywall. Much of modern environmental monitoring rests on that open archive."
    },
    {
      "type": "heading",
      "text": "How It Fits With Other Satellites"
    },
    {
      "type": "paragraph",
      "text": "Landsat is not alone up there. It works alongside programs like the European Sentinel satellites and a growing fleet of commercial imagers, each with different strengths in resolution, coverage, and revisit speed. But Landsat’s unmatched contribution is its history. No other program can show you the planet’s surface stretching back to the early 1970s with the same consistency."
    },
    {
      "type": "heading",
      "text": "Why It Is Worth Knowing About"
    },
    {
      "type": "paragraph",
      "text": "Understanding Landsat is really about understanding how we know what we know about a changing planet. The headlines about melting ice and disappearing forests are not guesses; they rest on decades of careful, repeated observation from orbit. Knowing where that imagery comes from makes you a sharper reader of every satellite map you encounter."
    },
    {
      "type": "heading",
      "text": "Landsat in Everyday Life"
    },
    {
      "type": "paragraph",
      "text": "You may already rely on Landsat without realizing it. Its imagery feeds into the basemaps behind popular mapping apps, helps farmers monitor the health of their fields, guides foresters tracking regrowth after wildfires, and supports city planners watching how urban areas expand. Cloud platforms now let researchers process the entire archive at once, asking questions across the whole planet that would once have taken lifetimes to answer by hand. A program that began in the early 1970s has quietly become part of the infrastructure of modern life."
    },
    {
      "type": "paragraph",
      "text": "That is the quiet power of a long, open data record: it keeps finding new uses that its original designers never imagined."
    },
    {
      "type": "paragraph",
      "text": "It also makes games built on satellite imagery more fun. Once you appreciate how much detail these instruments capture, a round of EarthGuessr becomes a chance to read the planet the way scientists do, one clue at a time."
    }
  ]
};
